import { Button, Icon, SimpleDialog, Tag, Text, Tooltip } from "@ttc3k/trekker";
import {
  EnumDKeyAssetType,
  ImageQualityRating,
  type AssetFile,
  type AssetImage,
} from "gql/generated";
import {
  ChatLines,
  ChatLinesSolid,
  Check,
  Download,
  MediaImage,
  Trash,
  WarningTriangle,
} from "iconoir-react";
import { useTranslation } from "react-i18next";
import { SmartImage } from "shared/components/atoms";
import { css } from "styled-system/css";
import { Flex } from "styled-system/jsx";
import { ImageMediaCaptionModal } from "./ImageMediaCaptionModal";
import { useImageMedia } from "./useImageMedia";
import { useEffect, useState } from "react";

export type ImageMediaProps = {
  image: Partial<AssetImage>;
  onDelete?: (image: Partial<AssetImage>) => void;
  onCaptionUpdated: (asset: Partial<AssetFile> | Partial<AssetImage>) => void;
  onModalToggle?: (isOpen: boolean) => void;
  showTag?: boolean;
  /** Thumbnail width in pixels. Defaults to 280. */
  thumbnailWidth?: number;
};

export const ImageMedia = ({
  image,
  onDelete,
  onCaptionUpdated,
  showTag = false,
  onModalToggle,
  thumbnailWidth = 280,
}: ImageMediaProps) => {
  const { t } = useTranslation();

  const {
    caption,
    isCaptionOpen,
    setIsCaptionOpen,
    handleDownload,
    canDownload,
  } = useImageMedia({ image, onAssetUpdated: onCaptionUpdated });

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    onModalToggle?.(isCaptionOpen || isDeleteConfirmOpen);
  }, [isCaptionOpen, isDeleteConfirmOpen, onModalToggle]);

  if (!image._id) {
    return <Icon Element={MediaImage} size="lg" color="icon.icon.light" />;
  }

  return (
    <>
      <Flex flexDir="column" width="full" gap="100" position="relative">
        {showTag && (
          <Tag
            visual="accent.blue"
            shape="rounded"
            Icon={Check}
            position="absolute"
            top="12px"
            left="12px"
            zIndex={1}
            pointerEvents="none"
          >
            {t("core:IMAGE.FEATURED")}
          </Tag>
        )}
        <SmartImage
          image={image}
          width={thumbnailWidth}
          shape="rounded"
          previewOnHover
          className={css({
            aspectRatio: "1",
            width: "100% !important",
            height: "auto !important",
            "& img, & video": {
              width: "100% !important",
              height: "100% !important",
            },
            ...(image.hasTransparency && {
              border: "1px solid token(colors.border.light)",
            }),
          })}
        />
        <Flex
          flexDir="row"
          justifyContent="space-between"
          alignItems="center"
          gap="250"
        >
          {image.qualityScore === ImageQualityRating.Low ? (
            <Flex display="flex" flexDir="row" alignItems="center" gap="100">
              <Icon Element={WarningTriangle} color="icon.warning.mid" />
              <Text color="text.warning.mid" visual="smallSemiBold">
                {t("core:IMAGE.LOW_QUALITY")}
              </Text>
            </Flex>
          ) : (
            <div></div>
          )}

          <Flex
            flexDir="row"
            justifyContent="flex-end"
            alignItems="center"
            gap="75"
          >
            {/* Only show caption icon if not in public widget ... this action causes the page to reload */}

            <Tooltip
              content={t("core:MEDIA.ICONS.CAPTION")}
              positioning={{ placement: "top" }}
            >
              <Button
                visual="unstyled"
                borderRadius="full"
                onClick={() => setIsCaptionOpen(true)}
                type="button"
              >
                <Icon Element={caption ? ChatLinesSolid : ChatLines} />
              </Button>
            </Tooltip>

            <Tooltip
              content={
                canDownload
                  ? t("core:MEDIA.ICONS.DOWNLOAD")
                  : t("core:MEDIA.ICONS.DOWNLOAD_UNAVAILABLE")
              }
              positioning={{ placement: "top" }}
            >
              <Button
                visual="unstyled"
                borderRadius="full"
                onClick={handleDownload}
                type="button"
                disabled={!canDownload}
                opacity={canDownload ? 1 : 0.4}
              >
                <Icon Element={Download} />
              </Button>
            </Tooltip>
            <Tooltip
              content={t("core:MEDIA.ICONS.DELETE")}
              positioning={{ placement: "top" }}
            >
              <Button
                visual="unstyled"
                borderRadius="full"
                onClick={() => setIsDeleteConfirmOpen(true)}
                type="button"
              >
                <Icon Element={Trash} />
              </Button>
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>

      <ImageMediaCaptionModal
        isOpen={isCaptionOpen}
        assetId={image._id}
        assetType={image.type as EnumDKeyAssetType}
        caption={caption}
        onClose={() => setIsCaptionOpen(false)}
        onCaptionUpdated={onCaptionUpdated}
      />

      <SimpleDialog
        open={isDeleteConfirmOpen}
        title={t("core:CONFIRM.TITLE")}
        message={t("core:MEDIA.CONFIRM_DELETE")}
        cancelButtonCopy={t("core:CONFIRM.NO")}
        onCancelClick={() => setIsDeleteConfirmOpen(false)}
        confirmButtonCopy={t("core:CONFIRM.YES")}
        confirmButtonVisual="destructive"
        onConfirmClick={() => {
          setIsDeleteConfirmOpen(false);
          onDelete?.(image);
        }}
        size="md"
      />
    </>
  );
};
